import { type AnyAction } from 'redux';
import {
	call,
	put,
	takeLatest,
	type CallEffect,
	type ForkEffect,
	type PutEffect,
	type SelectEffect
} from 'redux-saga/effects';

import { BuilderActionType } from './enums';

import { handleEmptyNullValues } from '@utilities';
import { normalizeSinkBcSidePanelHeights } from '@components/CabinetBuilder/utils';

import {
	updateCabinetState,
	fetchCabinetState,
	fetchCabinetCutlist,
	updateCabinetCutlist,
	getCabinetCalculation,
	updateCabinetMaterialsInBulk,
	getCounterTopCalculation,
	fetchCounterTopState,
	fetchLoftState,
	calculateLoftBuilder,
	fetchLoftCutlist,
	fetchFCBuilderData,
	fetchWallPanelState,
	getWallPanelCalculation,
	fetchWardrobeCalculation,
	fetchWardrobeState,
	fetchWardrobeCutlist,
	getElectricalCalculation,
	getProjectElectricalState,
	updateElectricalState,
	getPaintBuilderState,
	getPaintV2BuilderState,
	getPaintBuilderCalculation,
	calculatePaintV2Builder,
	getAllBuilderStates,
	getWallMouldingState,
	getWallMouldingCalculation,
	fetchWallMouldingBuilderState,
	updatePreDesignBuilder,
	fetchDrawerState as fetchDrawerStateApi,
	fetchDrawerCalculation,
	fetchDrawerCutlist,
	fetchLedgeState as fetchLedgeStateApi,
	fetchLedgeCalculation,
	fetchLedgeCutlist,
	fetchCurtainState,
	calculateCurtainBuilder,
	isCountertopStateNotFound
} from './api';

type BuilderSagaEffect = Generator<CallEffect<any> | PutEffect<any> | SelectEffect> | any;

type BuilderSagaFormEffect = Generator<ForkEffect<void>>;

const normalizeCabinetStateCutlist = (data: Record<string, any> | null | undefined) => {
	if (!data) return data;
	return {
		...data,
		cutlist: normalizeSinkBcSidePanelHeights(data?.cutlist || [], data?.builder, data?.height)
	};
};

export function* updateCabinetStateEffect(action: AnyAction): BuilderSagaEffect {
	try {
		const { cabinet, successCB } = action.payload;
		const data = handleEmptyNullValues(cabinet);
		yield call(updateCabinetState, data);
		successCB(data);
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_CABINET_STATE,
			payload: {
				error
			}
		});
	}
}

export function* fetchCabinetStateEffect(action: AnyAction): BuilderSagaEffect {
	const { productId, successCB, errorCB } = action.payload;
	try {
		const response = yield call(fetchCabinetState, productId);
		const data = normalizeCabinetStateCutlist(response.data);

		if (data) {
			yield put({
				type: BuilderActionType.STORE_CABINET_STATE,
				payload: {
					cabinet: data
				}
			});
			successCB(data);
		}
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_CABINET_STATE,
			payload: {
				error
			}
		});
		errorCB(error);
	}
}

export function* fetchCabinetCutlistEffect(action: AnyAction): BuilderSagaEffect {
	const { productId, successCB, errorCB } = action.payload;
	try {
		const response = yield call(fetchCabinetCutlist, productId);
		const data = normalizeCabinetStateCutlist(response.data);

		if (data) {
			yield put({
				type: BuilderActionType.STORE_CABINET_CUTLIST,
				payload: {
					cutlist: data.cutlist
				}
			});
			successCB(data.cutlist);
		}
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_CABINET_CUTLIST,
			payload: {
				error
			}
		});
		errorCB(error);
	}
}

export function* fetchLoftStateEffect(action: AnyAction): BuilderSagaEffect {
	const { productId, successCB, errorCB } = action.payload;
	try {
		const response = yield call(fetchLoftState, productId);
		const data = response.data;
		if (data) {
			yield put({
				type: BuilderActionType.STORE_LOFT_STATE,
				payload: { loft: data }
			});
			if (successCB) successCB(data);
		}
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_LOFT_STATE,
			payload: { error }
		});
		if (errorCB) errorCB(error);
	}
}

export function* fetchLoftCutlistEffect(action: AnyAction): BuilderSagaEffect {
	const { productId, successCB, errorCB } = action.payload;
	try {
		const response = yield call(fetchLoftCutlist, productId);
		const data = response.data;

		if (data) {
			yield put({
				type: BuilderActionType.STORE_LOFT_CUTLIST,
				payload: {
					cutlist: data.cutlist
				}
			});
			if (successCB) successCB(data.cutlist);
		}
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_LOFT_CUTLIST,
			payload: {
				error
			}
		});
		if (errorCB) errorCB(error);
	}
}

export function* fetchFCBuilderDataEffect(action: AnyAction): BuilderSagaEffect {
	const { productId, errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchFCBuilderData, productId);

		yield put({ type: BuilderActionType.STORE_BUILDER_FC_DATA, payload: data });
	} catch (error: any) {
		errorCB(error?.message);
	}
}

export function* updateCabinetCutlistEffect(action: AnyAction): BuilderSagaEffect {
	const { productId, cutlist, successCB, errorCB } = action.payload;
	try {
		const response = yield call(updateCabinetCutlist, productId, cutlist);
		if (successCB) successCB(response);
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_CABINET_CUTLIST,
			payload: {
				error
			}
		});
		if (errorCB) errorCB(error);
	}
}

export function* getCabinetCalculationEffect(action: AnyAction): BuilderSagaEffect {
	const { data, successCB, errorCB } = action.payload;
	try {
		const response = yield call(getCabinetCalculation, data);
		successCB({
			...response.data,
			cutlist: normalizeSinkBcSidePanelHeights(response.data?.cutlist || [], data?.builder, data?.height)
		});
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_CABINET_CALCULATION,
			payload: {
				error
			}
		});
		errorCB(error);
	}
}

export function* updateCabinetMaterialsInBulkEffect(action: AnyAction): BuilderSagaEffect {
	const { data, successCB, errorCB } = action.payload;
	try {
		const response = yield call(updateCabinetMaterialsInBulk, data);
		successCB(response.data);
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_CABINET_CALCULATION,
			payload: {
				error
			}
		});
		errorCB(error);
	}
}

export function* getLoftCalculationEffect(action: AnyAction): BuilderSagaEffect {
	const { data, successCB, errorCB } = action.payload;
	try {
		const response = yield call(calculateLoftBuilder, data);
		successCB(response.data);
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_LOFT_CALCULATION,
			payload: {
				error
			}
		});
		errorCB(error);
	}
}

export function* getCounterTopCalculationEffect(action: AnyAction): BuilderSagaEffect {
	const { data, successCB, errorCB } = action.payload;
	try {
		const response = yield call(getCounterTopCalculation, data);
		yield put({
			type: BuilderActionType.STORE_COUNTER_TOP_CALCULATION,
			payload: response.data
		});
		successCB(response.data);
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_COUNTER_TOP_CALCULATION,
			payload: {
				error
			}
		});
		errorCB(error);
	}
}

export function* getCounterTopStateEffect(action: AnyAction): BuilderSagaEffect {
	const { successCB, errorCB, productId, quotationId } = action.payload;
	try {
		const response = yield call(fetchCounterTopState, productId, quotationId);
		yield put({
			type: BuilderActionType.STORE_COUNTER_TOP_STATE,
			payload: response.data
		});
		if (successCB) successCB(response.data);
	} catch (error) {
		if (isCountertopStateNotFound(error)) {
			yield put({
				type: BuilderActionType.STORE_COUNTER_TOP_STATE,
				payload: {}
			});
			if (successCB) successCB({});
			return;
		}
		yield put({
			type: BuilderActionType.ERROR_COUNTER_TOP_STATE,
			payload: { error }
		});
		if (errorCB) errorCB(error);
	}
}

export function* fetchWallPanelStateEffect(action: AnyAction): BuilderSagaEffect {
	const { productId, successCB, errorCB } = action.payload;
	try {
		const response = yield call(fetchWallPanelState, productId);
		yield put({
			type: BuilderActionType.STORE_WALL_PANEL_STATE,
			payload: response.data
		});
		if (successCB) successCB(response.data);
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_WALL_PANEL_STATE,
			payload: error
		});
		if (errorCB) errorCB(error);
	}
}

export function* getWallPanelCalculationEffect(action: AnyAction): BuilderSagaEffect {
	const { data, successCB, errorCB } = action.payload;
	try {
		const response = yield call(getWallPanelCalculation, data);
		yield put({
			type: BuilderActionType.STORE_WALL_PANEL_CALCULATION,
			payload: response.data
		});
		if (successCB) {
			successCB(response.data);
		}
	} catch (error) {
		errorCB(error);
	}
}

export function* fetchWardrobeCalculationEffect(action: AnyAction): BuilderSagaEffect {
	const { data, successCB, errorCB } = action.payload;
	try {
		const response = yield call(fetchWardrobeCalculation, data?.values ? data.values : data);
		const responseData = response.data?.response
			? {
					...response.data.response,
					cutlist: response.data.cutlist?.cutlistItems ?? response.data.cutlist?.cutlist_items ?? []
				}
			: response.data;
		yield put({
			type: BuilderActionType.STORE_WARDROBE_CALCULATION,
			payload: responseData
		});
		if (successCB) {
			successCB(responseData);
		}
	} catch (error) {
		errorCB(error);
	}
}

export function* getElectricalCalculationEffect(action: AnyAction): BuilderSagaEffect {
	const { data, successCB, errorCB } = action.payload;
	try {
		const response = yield call(getElectricalCalculation, data);
		yield put({
			type: BuilderActionType.STORE_ELECTRICAL_CALCULATION,
			payload: response.data
		});
		if (successCB) {
			successCB(response.data);
		}
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_ELECTRICAL_CALCULATION,
			payload: {
				error
			}
		});
		errorCB(error);
	}
}

export function* fetchWardrobeStateEffect(action: AnyAction): BuilderSagaEffect {
	const { productId, successCB, errorCB } = action.payload;
	try {
		const response = yield call(fetchWardrobeState, productId);
		yield put({
			type: BuilderActionType.STORE_WARDROBE_STATE,
			payload: response.data
		});
		successCB(response.data);
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_WARDROBE_STATE,
			payload: error
		});
		errorCB(error);
	}
}

export function* fetchWardrobeCutlistEffect(action: AnyAction): BuilderSagaEffect {
	const { productId, successCB, errorCB } = action.payload;
	try {
		const response = yield call(fetchWardrobeCutlist, productId);
		const data = response.data;

		if (data) {
			yield put({
				type: BuilderActionType.STORE_WARDROBE_CUTLIST,
				payload: {
					wardrobeCutlist: data.cutlist
				}
			});
			successCB(data.cutlist);
		}
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_WARDROBE_CUTLIST,

			payload: {
				error
			}
		});
		errorCB(error);
	}
}

export function* getProjectElectricalStateEffect(action: AnyAction): BuilderSagaEffect {
	const { projectId, successCB, errorCB } = action.payload;
	try {
		const response = yield call(getProjectElectricalState, projectId);
		yield put({
			type: BuilderActionType.STORE_PROJECT_ELECTRICAL_STATE,
			payload: response.data
		});
		if (successCB) {
			successCB(response.data);
		}
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_PROJECT_ELECTRICAL_STATE,
			payload: {
				error
			}
		});
		errorCB(error);
	}
}

export function* updateElectricalStateEffect(action: AnyAction): BuilderSagaEffect {
	const { productId, data, successCB, errorCB } = action.payload;
	try {
		const response = yield call(updateElectricalState, productId, data);
		if (successCB) {
			successCB(response.data);
		}
	} catch (error) {
		errorCB(error);
	}
}

export function* getPaintBuilderStateEffect(action: AnyAction): BuilderSagaEffect {
	const { productId, successCB, errorCB, v2 } = action.payload;
	try {
		const response = yield call(v2 ? getPaintV2BuilderState : getPaintBuilderState, productId);
		yield put({
			type: BuilderActionType.STORE_PAINT_BUILDER_STATE,
			payload: response.data
		});
		if (successCB) {
			successCB(response.data);
		}
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_PAINT_BUILDER_STATE,
			payload: {
				error
			}
		});
		errorCB(error);
	}
}

export function* getPaintBuilderCalculationEffect(action: AnyAction): BuilderSagaEffect {
	const { data, successCB, errorCB, v2 } = action.payload;
	try {
		const response = yield call(v2 ? calculatePaintV2Builder : getPaintBuilderCalculation, data);

		yield put({
			type: BuilderActionType.STORE_PAINT_BUILDER_CALCULATION,
			payload: response.data
		});
		if (successCB) {
			successCB(response.data);
		}
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_PAINT_BUILDER_CALCULATION,
			payload: {
				error
			}
		});
		errorCB(error);
	}
}

export function* fetchAllBuilderStatesEffect(action: AnyAction): BuilderSagaEffect {
	const { quotationId, errorCB } = action.payload;
	try {
		const response = yield call(getAllBuilderStates, quotationId);
		const data = response.data;

		if (data) {
			yield put({
				type: BuilderActionType.STORE_ALL_BUILDER_STATES,
				payload: data.builderStates
			});
		}
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_ALL_BUILDER_STATES,
			payload: {
				error
			}
		});
		if (errorCB) {
			errorCB(error || 'Failed to fetch builder states');
		}
	}
}

export function* fetchAllBuilderStatesSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_ALL_BUILDER_STATES, fetchAllBuilderStatesEffect);
}

export function* getWallMouldingStateEffect(action: AnyAction): BuilderSagaEffect {
	const { productId, successCB, errorCB } = action.payload;
	try {
		const response = yield call(getWallMouldingState, productId);
		yield put({
			type: BuilderActionType.STORE_MOULDING_STATE,
			payload: response.data
		});
		if (successCB) {
			successCB(response.data);
		}
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_MOULDING_STATE,
			payload: {
				error
			}
		});
		if (errorCB) {
			errorCB(error || 'Failed to fetch builder states');
		}
	}
}

export function* fetchWallMouldingBuilderStateEffect(action: AnyAction): BuilderSagaEffect {
	const { productId, errorCB } = action.payload;
	try {
		const response = yield call(fetchWallMouldingBuilderState, productId);
		yield put({
			type: BuilderActionType.STORE_WALL_MOULDING_STATE,
			payload: response.data
		});
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_WALL_MOULDING_STATE,
			payload: { error }
		});
		if (errorCB) errorCB(error);
	}
}

export function* getWallMouldingCalculationEffect(action: AnyAction): BuilderSagaEffect {
	const { data, successCB, errorCB } = action.payload;
	try {
		const response = yield call(getWallMouldingCalculation, data);
		yield put({
			type: BuilderActionType.STORE_MOULDING_CALCULATION,
			payload: response.data
		});
		if (successCB) {
			successCB(response.data);
		}
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_MOULDING_CALCULATION,
			payload: {
				error
			}
		});
		errorCB(error);
	}
}

export function* updatePreDesignBuilderEffect(action: AnyAction): BuilderSagaEffect {
	const { productId, payload, successCB, errorCB } = action.payload;
	try {
		const response = yield call(updatePreDesignBuilder, productId, payload);
		if (successCB) successCB(response.data);
	} catch (error: any) {
		if (errorCB) {
			errorCB(error?.message);
		}
	}
}

export function* fetchCurtainStateEffect(action: AnyAction): BuilderSagaEffect {
	const { productId, successCB, errorCB } = action.payload;
	try {
		const response = yield call(fetchCurtainState, productId);
		yield put({
			type: BuilderActionType.STORE_CURTAIN_BUILDER_STATE,
			payload: response.data
		});
		if (successCB) successCB(response.data);
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_CURTAIN_BUILDER_STATE,
			payload: { error }
		});
		if (errorCB) errorCB(error);
	}
}

export function* getCurtainCalculationEffect(action: AnyAction): BuilderSagaEffect {
	const { data, successCB, errorCB } = action.payload;
	try {
		const response = yield call(calculateCurtainBuilder, data);
		yield put({
			type: BuilderActionType.STORE_CURTAIN_BUILDER_CALCULATION,
			payload: response.data
		});
		if (successCB) successCB(response.data);
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_CURTAIN_BUILDER_CALCULATION,
			payload: { error }
		});
		if (errorCB) errorCB(error);
	}
}

export function* fetchCabinetCutlistSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_CABINET_CUTLIST, fetchCabinetCutlistEffect);
}

export function* updateCabinetCutlistSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.UPDATE_CABINET_CUTLIST, updateCabinetCutlistEffect);
}

export function* updateCabinetStateSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.UPDATE_CABINET_STATE, updateCabinetStateEffect);
}

export function* fetchCabinetStateSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_CABINET_STATE, fetchCabinetStateEffect);
}

export function* getCabinetCalculationSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_CABINET_CALCULATION, getCabinetCalculationEffect);
}

export function* updateCabinetMaterialsInBulkSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_CABINET_BULK_MATERIAL_UPDATE, updateCabinetMaterialsInBulkEffect);
}

export function* getCounterTopCalculationSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_COUNTER_TOP_CALCULATION, getCounterTopCalculationEffect);
}

export function* getCounterTopStateSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_COUNTER_TOP_STATE, getCounterTopStateEffect);
}

export function* fetchLoftStateSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_LOFT_STATE, fetchLoftStateEffect);
}

export function* getLoftCalculationSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_LOFT_CALCULATION, getLoftCalculationEffect);
}

export function* fetchLoftCutlistSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_LOFT_CUTLIST, fetchLoftCutlistEffect);
}

export function* fetchFCBuilderDataSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_BUILDER_FC_DATA, fetchFCBuilderDataEffect);
}

export function* fetchWallMouldingBuilderStateSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_WALL_MOULDING_STATE, fetchWallMouldingBuilderStateEffect);
}

export function* fetchWallPanelStateSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_WALL_PANEL_STATE, fetchWallPanelStateEffect);
}

export function* getWallPanelCalculationSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_WALL_PANEL_CALCULATION, getWallPanelCalculationEffect);
}

export function* fetchWardrobeStateSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_WARDROBE_STATE, fetchWardrobeStateEffect);
}

export function* fetchWardrobeCalculationSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_WARDROBE_CALCULATION, fetchWardrobeCalculationEffect);
}

export function* fetchWardrobeCutlistSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_WARDROBE_CUTLIST, fetchWardrobeCutlistEffect);
}

export function* getElectricalCalculationSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_ELECTRICAL_CALCULATION, getElectricalCalculationEffect);
}

export function* getProjectElectricalStateSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_PROJECT_ELECTRICAL_STATE, getProjectElectricalStateEffect);
}

export function* updateElectricalStateSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.UPDATE_ELECTRICAL_STATE, updateElectricalStateEffect);
}

export function* getPaintBuilderCalculationSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_PAINT_BUILDER_CALCULATION, getPaintBuilderCalculationEffect);
}

export function* getPaintBuilderStateSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_PAINT_BUILDER_STATE, getPaintBuilderStateEffect);
}

export function* getWallMouldingStateSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_MOULDING_STATE, getWallMouldingStateEffect);
}

export function* getWallMouldingCalculationSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_MOULDING_CALCULATION, getWallMouldingCalculationEffect);
}

export function* updatePreDesignBuilderSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.UPDATE_PRE_DESIGN_BUILDER, updatePreDesignBuilderEffect);
}

// Drawer Builder Sagas
export function* fetchDrawerStateEffect(action: AnyAction): BuilderSagaEffect {
	const { productId, successCB, errorCB } = action.payload;
	try {
		const response = yield call(fetchDrawerStateApi, productId);
		yield put({
			type: BuilderActionType.STORE_DRAWER_STATE,
			payload: response.data
		});
		if (successCB) {
			successCB(response.data);
		}
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_DRAWER_STATE,
			payload: { error }
		});
		if (errorCB) errorCB(error);
	}
}

export function* fetchDrawerCalculationEffect(action: AnyAction): BuilderSagaEffect {
	const { data, successCB, errorCB } = action.payload;
	try {
		const response = yield call(fetchDrawerCalculation, data);
		yield put({
			type: BuilderActionType.STORE_DRAWER_CALCULATION,
			payload: response.data
		});
		if (successCB) {
			successCB(response.data);
		}
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_DRAWER_CALCULATION,
			payload: { error }
		});
		if (errorCB) errorCB(error);
	}
}

export function* fetchDrawerCutlistEffect(action: AnyAction): BuilderSagaEffect {
	const { productId, successCB, errorCB } = action.payload;
	try {
		const response = yield call(fetchDrawerCutlist, productId);
		yield put({
			type: BuilderActionType.STORE_DRAWER_CUTLIST,
			payload: response.data
		});
		if (successCB) {
			successCB(response.data);
		}
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_DRAWER_CUTLIST,
			payload: { error }
		});
		if (errorCB) errorCB(error);
	}
}

export function* fetchDrawerStateSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_DRAWER_STATE, fetchDrawerStateEffect);
}

export function* fetchDrawerCalculationSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_DRAWER_CALCULATION, fetchDrawerCalculationEffect);
}

export function* fetchDrawerCutlistSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_DRAWER_CUTLIST, fetchDrawerCutlistEffect);
}

// Ledge Builder Sagas
export function* fetchLedgeStateEffect(action: AnyAction): BuilderSagaEffect {
	const { productId, successCB, errorCB } = action.payload;
	try {
		const response = yield call(fetchLedgeStateApi, productId);
		yield put({
			type: BuilderActionType.STORE_LEDGE_STATE,
			payload: response.data
		});
		if (successCB) {
			successCB(response.data);
		}
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_LEDGE_STATE,
			payload: { error }
		});
		if (errorCB) errorCB(error);
	}
}

export function* fetchLedgeCalculationEffect(action: AnyAction): BuilderSagaEffect {
	const { data, successCB, errorCB } = action.payload;
	try {
		const response = yield call(fetchLedgeCalculation, data);
		yield put({
			type: BuilderActionType.STORE_LEDGE_CALCULATION,
			payload: response.data
		});
		if (successCB) {
			successCB(response.data);
		}
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_LEDGE_CALCULATION,
			payload: { error }
		});
		if (errorCB) errorCB(error);
	}
}

export function* fetchLedgeCutlistEffect(action: AnyAction): BuilderSagaEffect {
	const { productId, successCB, errorCB } = action.payload;
	try {
		const response = yield call(fetchLedgeCutlist, productId);
		yield put({
			type: BuilderActionType.STORE_LEDGE_CUTLIST,
			payload: response.data
		});
		if (successCB) {
			successCB(response.data);
		}
	} catch (error) {
		yield put({
			type: BuilderActionType.ERROR_LEDGE_CUTLIST,
			payload: { error }
		});
		if (errorCB) errorCB(error);
	}
}

export function* fetchLedgeStateSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_LEDGE_STATE, fetchLedgeStateEffect);
}

export function* fetchLedgeCalculationSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_LEDGE_CALCULATION, fetchLedgeCalculationEffect);
}

export function* fetchLedgeCutlistSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_LEDGE_CUTLIST, fetchLedgeCutlistEffect);
}

export function* fetchCurtainStateSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_CURTAIN_BUILDER_STATE, fetchCurtainStateEffect);
}

export function* getCurtainCalculationSaga(): BuilderSagaFormEffect {
	yield takeLatest(BuilderActionType.FETCH_CURTAIN_BUILDER_CALCULATION, getCurtainCalculationEffect);
}
